import { FiberManualRecord } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Link as MUILink,
  Radio,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { NextPage } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useState } from 'react';
import SEO from '../components/common/SEO';
import StaticPageThemeProvider from '../components/providers/StaticPageThemeProvider';
import emptyblock from '../public/images/empty-block.png';
import iphoneimage from '../public/images/iphone-users.png';
import { useUserCurrentQuery } from '../redux/services/auth/auth.generated';
import { usePlanQuery } from '../redux/services/payments/payments.generated';
import {
  GoPremiumBody,
  GoPremiumHeader,
  StaticPageSectionHeader,
} from '../components/common/StaticPageText';
import SubscriptionDetails from '../components/GoPremium/SubscriptionDetails';

import PremiumIcon2x from '/public/icons/common/premium-plan-2x.svg';
import Discover from '/public/images/go-premium/discover.png';
import Catalog from '/public/images/go-premium/ever-growing-catalog.png';
import Favourites from '/public/images/go-premium/favourites.png';
import Streaming from '/public/images/go-premium/high-quality-streaming.png';
import Payouts from '/public/images/go-premium/payouts.png';
import Prahar from '/public/images/go-premium/prahar.png';
import Cookies from 'js-cookie';
import Link from 'next/link';

// const CustomListIcon: FunctionComponent = ({ children }) => (
//   <Box
//     sx={{
//       backgroundColor: '#e9ecf5',
//       p: '2px 7px',
//       borderRadius: '5px',
//       textAlign: 'center',
//     }}
//   >
//     {children}
//   </Box>
// );

// const GoPremiumHeader: FunctionComponent<TypographyProps> = ({
//   children,
//   sx,
// }) => (
//   <Typography
//     variant={'h2'}
//     sx={{
//       fontSize: 22,
//       lineHeight: '24px',
//       letterSpacing: '0.22px',
//       my: 3,
//       ...sx,
//     }}
//   >
//     {children}
//   </Typography>
// );

// const GoPremiumBody: FunctionComponent<TypographyProps> = ({
//   children,
//   sx,
// }) => (
//   <Typography
//     variant={'h2'}
//     sx={{
//       lineHeight: '20px',
//       letterSpacing: '0.8px',
//       fontWeight: 400,
//       mb: 3,
//       ...sx,
//     }}
//   >
//     {children}
//   </Typography>
// );

const GoPremiumPage: NextPage = () => {
  const router = useRouter();

  const userToken = Cookies.get('ragya-token');
  const { data: userData } = useUserCurrentQuery({}, { skip: !userToken });

  const activeSubscription =
    userData?.userCurrent?.activeSubscription?.subscriptionPlan?.name;

  const { data: planData, isFetching } = usePlanQuery();

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  const [selectedPlanId, setSelectedPlanId] = useState(0);

  return (
    <StaticPageThemeProvider>
      <SEO title={'Ragya Premium Subscription'} />

      <Box>
        <Container maxWidth={'md'} sx={{ pt: 4 }}>
          {userData?.userCurrent?.activeSubscription?.isActive ? (
            <SubscriptionDetails userData={userData} />
          ) : (
            <Box
              sx={{
                borderRadius: '16px',
                color: 'white',
                p: 4,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 4,
                background: `linear-gradient(180deg, #0C4141 0%, #356969 100%)`,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    variant={'h2'}
                    sx={{ fontSize: '36px' }}
                    gutterBottom
                  >
                    Ragya Premium means the rest of the 99% of Ragya
                  </Typography>
                  <Typography
                    variant={'h4'}
                    sx={{
                      letterSpacing: '0.6px',
                      lineHeight: '18px',
                      fontSize: '14px',
                    }}
                    gutterBottom
                  >
                    Unlock all content in better quality, customise your prahar
                    experience, Browse and listen to your choice of playlists
                    organised by artistes, instruments and ragas, and more...
                  </Typography>
                  <Box>
                    <MUILink
                      underline={'none'}
                      sx={{ cursor: 'pointer' }}
                      href='#learnmoreaboutpremium'
                    >
                      <Typography
                        variant={'h4'}
                        component={'span'}
                        sx={{
                          letterSpacing: '0.2px',
                          lineHeight: '18px',
                          color: (theme) => theme.ragyaPalette.gray2,
                          fontSize: '14px',
                          marginTop: '16px',
                          display: 'block',
                        }}
                      >
                        Learn more about premium &darr;
                      </Typography>
                    </MUILink>
                  </Box>
                </Box>
              </Box>
              <Box flex={1}>
                <Typography
                  variant={'h2'}
                  sx={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '1.4px',
                  }}
                >
                  Choose a plan
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {isFetching ? (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mt: 2,
                      }}
                    >
                      <CircularProgress size={80} />
                    </Box>
                  ) : (
                    planData?.plans.map((plan) => (
                      <Box
                        key={plan.id}
                        sx={{
                          background: 'white',
                          borderRadius: '16px',
                          p: '16px',
                          width: '100%',
                          display: 'flex',
                        }}
                      >
                        <Radio
                          checked={selectedPlanId === plan.id}
                          onChange={() => setSelectedPlanId(plan.id)}
                        />
                        <Box>
                          <Typography
                            color={'primary'}
                            variant={'h2'}
                            sx={{ fontSize: '14px' }}
                            gutterBottom
                          >
                            {plan.name}
                          </Typography>
                          <Typography
                            color={'black'}
                            variant={'subtitle1'}
                            sx={{
                              fontSize: '16px',
                            }}
                          >
                            {plan.description}
                          </Typography>
                          <Typography color={'black'}>
                            {`${plan.currencySymbol}${plan.price}`}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
                <Button
                  disabled={
                    selectedPlanId === 0 || activeSubscription === 'Yearly'
                  }
                  variant='contained'
                  sx={{
                    borderRadius: '42px',
                    my: 2,
                    textTransform: 'none',
                    '&:disabled': {
                      color: (theme) => theme.ragyaPalette.gray5,
                    },
                  }}
                  onClick={() => {
                    if (selectedPlanId) {
                      userData?.userCurrent.id
                        ? router.push(`/pricing/${selectedPlanId}`)
                        : router.push('/login');
                    }
                  }}
                >
                  Select Plan and Proceed
                </Button>
                <br />
                <Link href={'/redeem'} passHref>
                  <MUILink sx={{ margin: 'auto', color: 'white', my: 2 }}>
                    Redeem a gift code
                  </MUILink>
                </Link>
              </Box>
            </Box>
          )}
          <Box sx={{ textAlign: 'center' }}>
            <Box mt={4} id='learnmoreaboutpremium'>
              <PremiumIcon2x />
            </Box>

            <StaticPageSectionHeader
              sx={{
                mt: 0,
                fontSize: '16px',
                fontWeight: 700,
                color: (theme) => theme.ragyaPalette.primary,
              }}
            >
              more about ragya premium
            </StaticPageSectionHeader>

            {/* 1. CATALOG */}

            <Box
              sx={{
                my: 8,
                display: 'flex',
                flexDirection: ['column', 'row'],
                gap: [4, 8],
              }}
            >
              {/* Column 1 */}
              <Box
                sx={{
                  textAlign: ['center', 'left'],
                  maxWidth: ['100%', '400px'],
                  flex: 1,
                  my: 'auto',
                }}
              >
                <GoPremiumHeader>An ever-growing Ragya catalog</GoPremiumHeader>
                <GoPremiumBody>
                  Ragya’s catalogue is ever-expanding with more and more
                  artistes and organisations bringing in their content on the
                  platform. Free offers a small preview catalogue and no
                  features whereas Premium offers the full and ever-increasing
                  catalogue of 1,000+ hours and full-features.
                </GoPremiumBody>
              </Box>

              {/* Column 2 */}
              <Box sx={{ flex: 1 }}>
                <Image src={Catalog} alt={'empty block'} layout='responsive' />
              </Box>
            </Box>

            {/* 2. PRAHAR */}

            <Box
              sx={{
                my: 8,
                display: 'flex',
                flexDirection: ['column', 'row'],
                gap: [4, 8],
              }}
            >
              {/* First Column */}
              <Box
                sx={{
                  textAlign: ['center', 'left'],
                  maxWidth: ['100%', '400px'],
                  flex: 1,
                  my: 'auto',
                }}
              >
                <GoPremiumHeader>
                  Customise Prahar player, your way
                </GoPremiumHeader>
                <GoPremiumBody>
                  With over <b>300+ ragas</b> organised by time of the day, you
                  can customise your Prahar player listening by vocal (male,
                  female) and/or instrumental music.
                </GoPremiumBody>
              </Box>

              {/* Second Column */}
              <Box sx={{ flex: 1 }}>
                <Image src={Prahar} alt={'empty block'} layout='responsive' />
              </Box>
            </Box>

            {/* 3. QUALITY */}

            <Box
              sx={{
                my: 8,
                display: 'flex',
                flexDirection: ['column', 'row'],
                gap: [4, 8],
              }}
            >
              {/* First Column */}
              <Box
                sx={{
                  flex: 1,
                  textAlign: ['center', 'left'],
                  maxWidth: '600px',
                  margin: 'auto',
                  my: 'auto',
                }}
              >
                <GoPremiumHeader>Higher Quality Streaming</GoPremiumHeader>
                <GoPremiumBody>
                  All music in Ragya Premium is at <b>192kbps</b> or higher as
                  compared to 32kbps in the free version.
                </GoPremiumBody>
              </Box>

              {/* Second Column */}
              <Box sx={{ flex: 1 }}>
                <Image
                  src={Streaming}
                  alt={'empty block'}
                  layout='responsive'
                />
              </Box>
            </Box>

            {/* 4. FAVOURITES */}

            <Box
              sx={{
                my: 8,
                display: 'flex',
                flexDirection: ['column', 'row'],
                gap: [4, 8],
              }}
            >
              {/* Second Column */}
              <Box
                sx={{
                  flex: 1,
                  textAlign: ['center', 'left'],
                  maxWidth: '600px',
                  my: 'auto',
                }}
              >
                <GoPremiumHeader>
                  Listen to just your favourites!
                </GoPremiumHeader>
                <GoPremiumBody>
                  Premium unlocks the ability to listen to your favourite music
                  on Ragya.
                </GoPremiumBody>
              </Box>

              {/* First Column */}
              <Box sx={{ flex: 1 }}>
                <Image
                  src={Favourites}
                  alt={'empty block'}
                  layout='responsive'
                />
              </Box>
            </Box>

            {/* 5. ARTISTE SHARE */}

            <Box
              sx={{
                my: 8,
                display: 'flex',
                flexDirection: ['column', 'row'],
                gap: [4, 8],
              }}
            >
              {/* First Column */}
              <Box
                sx={{
                  flex: 1,
                  textAlign: ['center', 'left'],
                  maxWidth: '600px',
                  margin: 'auto',
                }}
              >
                <GoPremiumHeader>
                  {"Ragya's fair artiste payout mode"}
                </GoPremiumHeader>
                <GoPremiumBody>
                  We share subscription revenue with artists, paying them based
                  on the time you listen. The more you enjoy Ragya, the more
                  artists earn.
                </GoPremiumBody>
              </Box>

              {/* Second Column */}
              <Box sx={{ flex: 1 }}>
                <Image src={Payouts} alt={'empty block'} layout='responsive' />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              background: 'white',
              borderRadius: 2,
              border: (theme) =>
                `1px solid var(${theme.ragyaPalette.gray1}, #D3E4E4)`,
              boxShadow: '0px 0px 0px 0px rgba(0, 0, 0, 0.14)',
              p: 4,
              gap: 3,
            }}
            id={'premium-upgrade-ios'}
          >
            <Box>
              <Image src={iphoneimage} alt={'iphone'} />
            </Box>
            <Box>
              <Typography variant={'h2'} mb={3}>
                How to upgrade to Premium on iPhone
              </Typography>
              <Typography
                component={'ol'}
                mb={3}
                sx={{
                  fontSize: '14px',
                  letterSpacing: '0.6px',
                }}
              >
                <li>{`Sign up or login to your Ragya account on ragya.com`}</li>
                <li>{'Choose your subscription plan and make the payment'}</li>
                <li>{'Log out of the Ragya website'}</li>
                <li>
                  {
                    'Launch the Ragya mobile app on your iPhone/iPad. Login and access Ragya Premium using the same login method (Email + Password OR Google / Facebook / Apple) you used when you purchased Ragya premium.'
                  }
                </li>
                <li>{'Your Premium subscription will be active now'}</li>
              </Typography>
              <Typography
                color={(theme) => theme.ragyaPalette.gray7}
                sx={{
                  fontSize: '14px',
                  letterSpacing: '0.6px',
                }}
              >
                In case the problem persists, please write to us at{' '}
                <MUILink underline={'none'} href={'mailto:support@ragya.com'}>
                  support@ragya.com
                </MUILink>{' '}
                and we’ll resolve the issue as soon as we can.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </StaticPageThemeProvider>
  );
};

export default GoPremiumPage;
